import { Pagination } from "types/Models/pagination";
import { emptyApi } from "./emptyApi";

export type RecognitionWaterfallKind = "REVENUE" | "EXPENSE";

// Amounts per month line up index-for-index with `periods`
export type RecognitionWaterfallAmounts = {
  prior_recognized: number | string;
  periodic: (number | string)[];
  deferred: number | string;
  recognized: number | string;
  scheduled: number | string;
};

export type RecognitionWaterfallItem = RecognitionWaterfallAmounts & {
  item_id: string;
  description: string | null;
  billing_period_start_date: string | null;
  billing_period_end_date: string | null;
};

export type RecognitionWaterfallDocument = RecognitionWaterfallAmounts & {
  document_id: string;
  document_number: string | null;
  document_date: string;
  status: string;
  items: RecognitionWaterfallItem[];
};

export type RecognitionWaterfallParty = RecognitionWaterfallAmounts & {
  party_id: string | null;
  party_name: string;
  documents: RecognitionWaterfallDocument[];
};

export type RecognitionWaterfallPeriod = {
  period: string;
  period_start: string;
  period_end: string;
};

export type RecognitionWaterfall = {
  periods: RecognitionWaterfallPeriod[];
  parties: RecognitionWaterfallParty[];
  totals: RecognitionWaterfallAmounts;
} & Pagination;

// The table's column ids the API can sort by; months are period_<index within the range>
export type RecognitionWaterfallSortColumn =
  | "name"
  | "prior_recognized"
  | "deferred"
  | "scheduled"
  | `period_${number}`;

// A leading - sorts descending
export type RecognitionWaterfallOrdering =
  | RecognitionWaterfallSortColumn
  | `-${RecognitionWaterfallSortColumn}`;

export type RecognitionWaterfallParams = {
  entityId: string;
  kind: RecognitionWaterfallKind;
  start_date: string;
  end_date: string;
  search_term?: string;
  ordering?: RecognitionWaterfallOrdering;
  page_num?: number;
  page_size?: number;
};

const WATERFALL_PATH: Record<RecognitionWaterfallKind, string> = {
  REVENUE: "revenue_schedule",
  EXPENSE: "expense_schedule",
};

export const recognitionWaterfallApis = emptyApi.injectEndpoints({
  endpoints: (build) => ({
    getRecognitionWaterfall: build.query<
      RecognitionWaterfall,
      RecognitionWaterfallParams
    >({
      query: ({ entityId, kind, ...params }) => ({
        url: `/api/inkle/bookkeeping/entity/${entityId}/${WATERFALL_PATH[kind]}/waterfall/`,
        params,
      }),
      // Editing an invoice or bill from the cell slider's link must refresh the amounts here
      providesTags: (result) => (result ? ["INVOICES", "VENDOR_BILL"] : []),
    }),
  }),
});

export const { useGetRecognitionWaterfallQuery } = recognitionWaterfallApis;
